import { useEffect, useRef, useState } from 'react';

export type NgDataListSelectProps<TItem> = {
  value?: TItem;
  onValueChanged?: (value: TItem | undefined) => void | Promise<void>;
  items?: readonly TItem[];
  label?: string;
  nullText?: string;
  textSelector?: (item: TItem) => string | undefined;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  dataListId?: string;
  testID?: string;
  labelTestID?: string;
};

export function NgDataListSelect<TItem>({
  onValueChanged,
  items = [],
  label,
  textSelector = (item) => String(item),
  placeholder,
  disabled,
  id,
  dataListId,
  testID,
  labelTestID,
}: NgDataListSelectProps<TItem>) {
  const [generatedId] = useState(() => `ng-input-${crypto.randomUUID().replaceAll('-', '')}`);
  const [generatedDataListId] = useState(
    () => `ng-datalist-${crypto.randomUUID().replaceAll('-', '')}`,
  );
  const effectiveId = id ?? generatedId;
  const effectiveDataListId = dataListId ?? generatedDataListId;

  const inputRef = useRef<HTMLInputElement>(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const textSelectorRef = useRef(textSelector);
  textSelectorRef.current = textSelector;
  const onValueChangedRef = useRef(onValueChanged);
  onValueChangedRef.current = onValueChanged;

  useEffect(() => {
    const input = inputRef.current;
    if (!input) {
      return;
    }

    const handleChange = () => {
      const text = input.value;
      const selected = itemsRef.current.find((item) => textSelectorRef.current(item) === text);
      void onValueChangedRef.current?.(selected);
    };
    input.addEventListener('change', handleChange);
    return () => input.removeEventListener('change', handleChange);
  }, []);

  return (
    <>
      {label != null && label.trim() !== '' && (
        <label htmlFor={effectiveId} className="form-label" data-testid={labelTestID}>
          {label}
        </label>
      )}

      <input
        ref={inputRef}
        className="form-control"
        list={effectiveDataListId}
        id={effectiveId}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testID}
      />

      <datalist id={effectiveDataListId}>
        {items.map((item, index) => (
          <option key={index} value={textSelector(item) ?? ''}></option>
        ))}
      </datalist>
    </>
  );
}
